import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, Sparkles } from 'lucide-react'
import { sendWaitlistNotification } from '../utils/emailService'

interface Message {
  id: number
  text: string
  sender: 'bot' | 'user'
  timestamp: Date
}

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Removed auto-open functionality - chatbot only opens on user click

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleToggle = () => {
    if (!isOpen) {
      setIsOpen(true)
      if (messages.length === 0) {
        setMessages([
          {
            id: 1,
            text: "Hi there! 👋 We're excited to announce that Azrefy will be live in just a few weeks!",
            sender: 'bot',
            timestamp: new Date()
          },
          {
            id: 2,
            text: "Want to be among the first to experience smart finance management? Share your email and we'll add you to our exclusive waitlist! 🚀",
            sender: 'bot',
            timestamp: new Date()
          }
        ])
      }
    } else {
      setIsOpen(false)
      sessionStorage.setItem('chatbotDismissed', 'true')
    }
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !email.includes('@')) {
      const errorMsg: Message = {
        id: messages.length + 1,
        text: "Oops! Please enter a valid email address. 📧",
        sender: 'bot',
        timestamp: new Date()
      }
      setMessages([...messages, errorMsg])
      return
    }

    setIsSubmitting(true)

    // Add user message
    const userMsg: Message = {
      id: messages.length + 1,
      text: email,
      sender: 'user',
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMsg])

    // Store in localStorage and send email notification
    setTimeout(async () => {
      localStorage.setItem('azrefy_waitlist_email', email)
      
      // Send email notification to contact@azrefy.com
      await sendWaitlistNotification(email)
      
      const successMsg: Message = {
        id: messages.length + 2,
        text: "🎉 Awesome! You're on the waitlist! We'll send you an email as soon as we launch. Get ready for a revolutionary finance experience!",
        sender: 'bot',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, successMsg])
      setIsSubmitting(false)
      setHasSubmitted(true)
      setEmail('')

      // Auto-close after success
      setTimeout(() => {
        setIsOpen(false)
      }, 4000)
    }, 1500)
  }

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleToggle}
            className="fixed bottom-6 right-6 z-40 bg-transparent shadow-2xl hover:shadow-blue-500/50 transition-all"
          >
            <motion.img 
              src="/logos/azrefy-icon.svg" 
              alt="Chat" 
              className="w-24 h-24 drop-shadow-2xl"
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-32px)] h-[500px] sm:h-[550px] lg:h-[600px] max-h-[calc(100vh-140px)] sm:max-h-[calc(100vh-120px)] lg:max-h-[calc(100vh-100px)] bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-800"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 flex items-center justify-between relative">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-white blur-md opacity-50 rounded-lg"></div>
                  <img
                    src="/logos/azrefy-icon.svg"
                    alt="Azrefy"
                    className="w-10 h-10 rounded-lg relative z-10"
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Azrefy</h3>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white/90 text-xs">Online</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleToggle}
                className="text-white hover:text-white/80 transition-colors p-3 min-w-[48px] min-h-[48px] flex items-center justify-center flex-shrink-0 relative z-50 active:bg-white/10 rounded-lg touch-manipulation"
                aria-label="Close chat"
                type="button"
              >
                <X size={28} strokeWidth={2.5} />
              </button>
            </div>

            {/* Messages */}
            <div className="h-[calc(100%-140px)] overflow-y-auto p-4 space-y-4">
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'bg-gray-800 text-gray-100'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.text}</p>
                  </div>
                </motion.div>
              ))}
              
              {isSubmitting && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-800 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        className="w-2 h-2 bg-blue-500 rounded-full"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        className="w-2 h-2 bg-purple-500 rounded-full"
                      />
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        className="w-2 h-2 bg-pink-500 rounded-full"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {!hasSubmitted && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800">
                <form onSubmit={handleEmailSubmit} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email..."
                    className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                    disabled={isSubmitting}
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-xl hover:shadow-lg transition-shadow disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Sparkles size={20} className="animate-spin" />
                    ) : (
                      <Send size={20} />
                    )}
                  </motion.button>
                </form>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Chatbot
